/**
 * Support ratings for glTF extensions, as they apply to the Trace viewer's
 * Babylon.js + WebGL/WebXR runtime. A creator uploading a model should know
 * whether an extension it relies on will render faithfully, degrade silently,
 * or not be understood at all.
 *
 * This is a hand-maintained list — extend it as the runtime's support changes.
 * Anything not listed is treated as 'unknown' (surfaced as a soft caution).
 */

export type ExtensionSupport =
  /** Fully handled by the runtime; safe to use. */
  | 'supported'
  /** Understood but with caveats (perf, partial features). */
  | 'partial'
  /** Not handled — the feature is ignored, so the model may look wrong. */
  | 'unsupported'
  /** Not in our list — we can't vouch for it either way. */
  | 'unknown';

export interface ExtensionRating {
  support: ExtensionSupport;
  /** One-line, creator-facing note on what this means for their model. */
  note: string;
}

/**
 * Known extensions and how the runtime treats them. Keep notes short and
 * grounded in what the creator should *do* (re-export, ignore, etc.).
 */
const EXTENSION_RATINGS: Record<string, ExtensionRating> = {
  // Ratings reflect support across ALL Trace clients (the web/Babylon viewer
  // AND the Unity app), not just one renderer. An extension only one client
  // understands renders wrong elsewhere, so we keep this conservative: list an
  // extension as 'supported' only when every client handles it. Everything else
  // defaults to 'unknown' (a soft caution) until verified cross-client.
  KHR_mesh_quantization: {
    support: 'supported',
    note: 'Compact vertex attributes — well supported (the optimizer applies it too).',
  },
  KHR_texture_transform: {
    support: 'supported',
    note: 'UV offset/scale/rotation — well supported.',
  },
  TRACE_materials_occlusion: {
    support: 'supported',
    // Trace's own extension: the Unity app and visionOS shipped it first, the
    // web viewer since the TRACE_materials_occlusion loader extension landed.
    note: 'Trace occluder material — writes depth only in AR and play mode so real-world geometry hides virtual content behind it. Shows as black in the editor.',
  },
  KHR_draco_mesh_compression: {
    support: 'partial',
    // Draco needs a decoder the client must ship separately, so support is
    // per-runtime rather than universal. The optimizer decodes it and writes
    // the geometry uncompressed.
    note: 'Compressed geometry that needs a Draco decoder — not every runtime ships one. The optimizer removes it; quantization gets most of the saving and is understood everywhere.',
  },
  EXT_meshopt_compression: {
    support: 'partial',
    // Like Draco: a decoder per runtime (gltfpack output). The optimizer
    // decodes it and writes the geometry uncompressed.
    note: 'Compressed geometry that needs a meshopt decoder — not every runtime ships one. The optimizer removes it; quantization gets most of the saving and is understood everywhere.',
  },
};

/** Look up an extension's rating, defaulting to 'unknown' for anything unlisted. */
export function rateExtension(name: string): ExtensionRating {
  return (
    EXTENSION_RATINGS[name] ?? {
      support: 'unknown',
      note: 'Not on our verified list — it may not be fully supported in AR. Test before publishing.',
    }
  );
}

/** True if a dimension is a power of two (1, 2, 4, …, 1024, 2048). NPOT below. */
export function isPowerOfTwo(n: number): boolean {
  return Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;
}

/**
 * A texture is "NPOT" (non-power-of-two) if either side isn't a power of two
 * (e.g. 1000×500). NPOT textures can't use mipmaps or repeat wrapping on some
 * GPUs/WebGL1, may be upscaled to the next POT in memory, and generally render
 * less efficiently — so we flag them and suggest resizing to POT.
 */
export function isNonPowerOfTwo(width?: number, height?: number): boolean {
  if (!width || !height) return false;
  return !isPowerOfTwo(width) || !isPowerOfTwo(height);
}
